'use client';

import { useTranslations } from 'next-intl';

import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

import type { OcrProgressStage } from '../types';

interface OcrProgressBarProps {
  currentPage: number;
  totalPages: number;
  stage: OcrProgressStage | null;
  /** Compact variant skips the page/stage caption — used in inline table cells. */
  compact?: boolean;
  className?: string;
}

const STAGE_KEY: Record<OcrProgressStage, string> = {
  preparing: 'progressStage.preparing',
  uploading: 'progressStage.uploading',
  extracting: 'progressStage.extracting',
  parsing: 'progressStage.parsing',
  storing: 'progressStage.storing'
};

export function OcrProgressBar({
  currentPage,
  totalPages,
  stage,
  compact,
  className
}: OcrProgressBarProps) {
  const t = useTranslations('ocr');

  const safeTotal = Math.max(0, totalPages);
  const safeCurrent = Math.max(0, Math.min(currentPage, safeTotal));
  const percent =
    safeTotal > 0 ? Math.round((safeCurrent / safeTotal) * 100) : 0;

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      <Progress value={percent} className={cn('h-1.5', compact && 'h-1')} />
      {!compact && (
        <div className='text-muted-foreground flex items-center justify-between text-[11px] leading-none'>
          <span>
            {safeTotal > 0
              ? t('pageOfPages', { current: safeCurrent, total: safeTotal })
              : t('preparing')}
          </span>
          <span className='text-foreground/80 font-medium'>
            {stage ? t(STAGE_KEY[stage]) : `${percent}%`}
          </span>
        </div>
      )}
    </div>
  );
}
